import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { useCart } from '../contexts/CartContext';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { formatZAR } from '../utils/currency';
import { Building2, CreditCard, Copy, Check, Upload, FileText, AlertCircle, CheckCircle2, X } from 'lucide-react';
export function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cart,
    clearCart
  } = useCart();
  const {
    addOrder
  } = useData();
  const {
    user
  } = useAuth();
  const [proofOfPayment, setProofOfPayment] = useState<string | null>(null);
  const [proofFileName, setProofFileName] = useState<string>('');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Get order details from location state
  const orderDetails = location.state as {
    cartTotal: number;
    deliveryFee: number;
    totalWithDelivery: number;
    isWithinHeidelberg: boolean;
  };
  if (!orderDetails || cart.length === 0) {
    navigate('/cart');
    return null;
  }
  const bankDetails = {
    bankName: 'Capitec Bank',
    accountName: 'Bloom and Pop',
    accountType: 'Transact',
    accountNumber: '1054784108',
    branchCode: '450105'
  };
  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image (JPG, PNG) or PDF file');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setProofOfPayment(reader.result as string);
      setProofFileName(file.name);
    };
    reader.readAsDataURL(file);
  };
  const handleRemoveProof = () => {
    setProofOfPayment(null);
    setProofFileName('');
  };
  const handleSubmitOrder = async () => {
    if (!proofOfPayment) {
      alert('Please upload proof of payment before submitting');
      return;
    }
    if (!user) return;
    setIsSubmitting(true);
    try {
      // Create order with all details
      const newOrder = {
        userId: user.id,
        userRegion: user.region,
        customerName: `${user.name} ${user.surname}`,
        customerEmail: user.email,
        customerPhone: user.cellNumber,
        customerWhatsapp: user.whatsappNumber,
        deliveryType: 'delivery' as const,
        deliveryDate: cart[0]?.deliveryDate || new Date().toISOString(),
        preferredTime: cart[0]?.preferredTime || '10:00',
        deliveryFee: orderDetails.deliveryFee,
        items: cart.map(item => ({
          productId: item.product.id,
          name: item.product.name,
          quantity: item.quantity,
          price: item.product.price,
          customMessage: item.customMessage,
          deliveryDate: item.deliveryDate,
          deliveryType: item.deliveryType,
          deliveryAddress: item.deliveryAddress,
          preferredTime: item.preferredTime,
          recipientName: item.recipientName,
          recipientPhone: item.recipientPhone,
          isGift: item.isGift
        })),
        total: orderDetails.totalWithDelivery,
        proofOfPayment: proofOfPayment,
        proofOfPaymentFileName: proofFileName,
        status: 'Pending' as const,
        date: new Date().toISOString()
      };
      addOrder(newOrder);
      clearCart();
      // Show success and redirect
      alert('✅ Order placed successfully! We will verify your payment and confirm your order shortly.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Failed to submit order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-1922">
      <Navbar data-id="element-1923" />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1924">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-1925">
          {/* Header */}
          <div className="text-center mb-8" data-id="element-1926">
            <h1 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-id="element-1927">
              Complete Your Payment
            </h1>
            <p className="text-gray-600" data-id="element-1928">
              Make your payment and upload proof to complete your order
            </p>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-6" data-id="element-1929">
            <h2 className="font-bold text-lg text-gray-900 mb-4" data-id="element-1930">
              Order Summary
            </h2>
            <div className="space-y-2 text-sm" data-id="element-1931">
              <div className="flex justify-between text-gray-600" data-id="element-1932">
                <span data-id="element-1933">Subtotal ({cart.length} items)</span>
                <span className="font-medium" data-id="element-1934">
                  {formatZAR(orderDetails.cartTotal)}
                </span>
              </div>
              <div className="flex justify-between text-gray-600" data-id="element-1935">
                <span data-id="element-1936">Delivery Fee</span>
                <span className="font-medium" data-id="element-1937">
                  {formatZAR(orderDetails.deliveryFee)}
                </span>
              </div>
              <div className="flex justify-between text-xl font-bold pt-3 border-t border-gray-200" data-id="element-1938">
                <span data-id="element-1939">Total to Pay</span>
                <span className="text-rose-500" data-id="element-1940">
                  {formatZAR(orderDetails.totalWithDelivery)}
                </span>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 md:p-8 shadow-sm border border-blue-200 mb-6" data-id="element-1941">
            <div className="flex items-center gap-3 mb-6" data-id="element-1942">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1943">
                <Building2 className="w-6 h-6 text-blue-600" data-id="element-1944" />
              </div>
              <div data-id="element-1945">
                <h2 className="font-bold text-xl text-gray-900" data-id="element-1946">
                  Bank Transfer Details
                </h2>
                <p className="text-sm text-gray-600" data-id="element-1947">
                  Use these details to make your payment
                </p>
              </div>
            </div>

            <div className="space-y-4" data-id="element-1948">
              {/* Bank Name */}
              <div className="bg-white rounded-xl p-4" data-id="element-1949">
                <div className="flex items-center justify-between" data-id="element-1950">
                  <div data-id="element-1951">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-1952">Bank Name</p>
                    <p className="font-bold text-gray-900" data-id="element-1953">
                      {bankDetails.bankName}
                    </p>
                  </div>
                  <button onClick={() => handleCopy(bankDetails.bankName, 'bankName')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1954">
                    {copiedField === 'bankName' ? <Check className="w-5 h-5 text-green-600" data-id="element-1955" /> : <Copy className="w-5 h-5 text-gray-400" data-id="element-1956" />}
                  </button>
                </div>
              </div>

              {/* Account Name */}
              <div className="bg-white rounded-xl p-4" data-id="element-1957">
                <div className="flex items-center justify-between" data-id="element-1958">
                  <div data-id="element-1959">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-1960">Account Name</p>
                    <p className="font-bold text-gray-900" data-id="element-1961">
                      {bankDetails.accountName}
                    </p>
                  </div>
                  <button onClick={() => handleCopy(bankDetails.accountName, 'accountName')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1962">
                    {copiedField === 'accountName' ? <Check className="w-5 h-5 text-green-600" data-id="element-1963" /> : <Copy className="w-5 h-5 text-gray-400" data-id="element-1964" />}
                  </button>
                </div>
              </div>

              {/* Account Type */}
              <div className="bg-white rounded-xl p-4" data-id="element-1965">
                <div className="flex items-center justify-between" data-id="element-1966">
                  <div data-id="element-1967">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-1968">Account Type</p>
                    <p className="font-bold text-gray-900" data-id="element-1969">
                      {bankDetails.accountType}
                    </p>
                  </div>
                  <button onClick={() => handleCopy(bankDetails.accountType, 'accountType')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1970">
                    {copiedField === 'accountType' ? <Check className="w-5 h-5 text-green-600" data-id="element-1971" /> : <Copy className="w-5 h-5 text-gray-400" data-id="element-1972" />}
                  </button>
                </div>
              </div>

              {/* Account Number */}
              <div className="bg-white rounded-xl p-4" data-id="element-1973">
                <div className="flex items-center justify-between" data-id="element-1974">
                  <div data-id="element-1975">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-1976">Account Number</p>
                    <p className="font-bold text-gray-900 text-lg" data-id="element-1977">
                      {bankDetails.accountNumber}
                    </p>
                  </div>
                  <button onClick={() => handleCopy(bankDetails.accountNumber, 'accountNumber')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1978">
                    {copiedField === 'accountNumber' ? <Check className="w-5 h-5 text-green-600" data-id="element-1979" /> : <Copy className="w-5 h-5 text-gray-400" data-id="element-1980" />}
                  </button>
                </div>
              </div>

              {/* Branch Code */}
              <div className="bg-white rounded-xl p-4" data-id="element-1981">
                <div className="flex items-center justify-between" data-id="element-1982">
                  <div data-id="element-1983">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-1984">Branch Code</p>
                    <p className="font-bold text-gray-900" data-id="element-1985">
                      {bankDetails.branchCode}
                    </p>
                  </div>
                  <button onClick={() => handleCopy(bankDetails.branchCode, 'branchCode')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1986">
                    {copiedField === 'branchCode' ? <Check className="w-5 h-5 text-green-600" data-id="element-1987" /> : <Copy className="w-5 h-5 text-gray-400" data-id="element-1988" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-100 rounded-xl" data-id="element-1989">
              <div className="flex items-start gap-2" data-id="element-1990">
                <AlertCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" data-id="element-1991" />
                <p className="text-sm text-blue-900" data-id="element-1992">
                  <strong data-id="element-1993">Important:</strong> Please use your order reference or
                  name when making the payment so we can easily identify your
                  transaction.
                </p>
              </div>
            </div>
          </div>

          {/* Upload Proof of Payment */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200 mb-6" data-id="element-1994">
            <div className="flex items-center gap-3 mb-6" data-id="element-1995">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center" data-id="element-1996">
                <Upload className="w-6 h-6 text-rose-600" data-id="element-1997" />
              </div>
              <div data-id="element-1998">
                <h2 className="font-bold text-xl text-gray-900" data-id="element-1999">
                  Upload Proof of Payment
                </h2>
                <p className="text-sm text-gray-600" data-id="element-2000">
                  Upload your payment confirmation or receipt
                </p>
              </div>
            </div>

            {!proofOfPayment ? <label htmlFor="proof-upload" className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-2001">
                <div className="flex flex-col items-center justify-center pt-5 pb-6" data-id="element-2002">
                  <Upload className="w-12 h-12 text-gray-400 mb-4" data-id="element-2003" />
                  <p className="mb-2 text-sm text-gray-500" data-id="element-2004">
                    <span className="font-semibold" data-id="element-2005">Click to upload</span> or
                    drag and drop
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-2006">
                    PNG, JPG or PDF (Max 5MB)
                  </p>
                </div>
                <input id="proof-upload" type="file" className="hidden" accept="image/png,image/jpeg,image/jpg,application/pdf" onChange={handleFileUpload} data-id="element-2007" />
              </label> : <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6" data-id="element-2008">
                <div className="flex items-center justify-between" data-id="element-2009">
                  <div className="flex items-center gap-4" data-id="element-2010">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-2011">
                      <FileText className="w-6 h-6 text-green-600" data-id="element-2012" />
                    </div>
                    <div data-id="element-2013">
                      <p className="font-medium text-gray-900" data-id="element-2014">
                        {proofFileName}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2015">
                        Proof of payment uploaded
                      </p>
                    </div>
                  </div>
                  <button onClick={handleRemoveProof} className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600" data-id="element-2016">
                    <X className="w-5 h-5" data-id="element-2017" />
                  </button>
                </div>
              </div>}

            <div className="mt-4 p-4 bg-yellow-50 rounded-xl border border-yellow-200" data-id="element-2018">
              <p className="text-sm text-yellow-900" data-id="element-2019">
                <strong data-id="element-2020">Note:</strong> Your order will be processed once we
                verify your payment. This usually takes a few hours during
                business hours.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4" data-id="element-2021">
            <Button variant="outline" onClick={() => navigate('/cart')} className="flex-1" data-id="element-2022">
              Back to Cart
            </Button>
            <Button onClick={handleSubmitOrder} disabled={!proofOfPayment || isSubmitting} className="flex-1" size="lg" rightIcon={<CheckCircle2 data-id="element-2024" />} data-id="element-2023">
              {isSubmitting ? 'Submitting...' : 'Submit Order'}
            </Button>
          </div>

          {/* Help Section */}
          <div className="mt-8 text-center" data-id="element-2025">
            <p className="text-sm text-gray-600" data-id="element-2026">
              Need help with payment?{' '}
              <a href="/contact" className="text-rose-600 hover:text-rose-700 font-medium" data-id="element-2027">
                Contact us
              </a>
            </p>
          </div>
        </motion.div>
      </div>

      <Footer data-id="element-2028" />
    </div>;
}